import { API_BASE_URL } from "../config/api";
import { getToken } from "../shared/data/storage/authStorage";

export interface TallyCompany {
  id: number;
  company_name: string;
  tally_host: string | null;
  tally_port: number | null;
  tally_company_name: string | null;
  accounting_backend: string;
}

export interface SparkRunResult {
  command: string;
  output: string | null;
  [k: string]: unknown;
}

export type TaxType = "gst" | "tds" | "tcs" | "all";

const BASE = `${API_BASE_URL}/api/admin/tally`;

function authHeaders(): HeadersInit {
  const token = getToken();
  return {
    Authorization: token ? `Bearer ${token}` : "",
    Accept: "application/json",
  };
}

async function get<T>(path: string, params: Record<string, string | number | undefined> = {}): Promise<T> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") qs.set(k, String(v));
  }
  const url = qs.toString() ? `${BASE}${path}?${qs}` : `${BASE}${path}`;
  const res = await fetch(url, { method: "GET", headers: authHeaders() });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`${res.status} ${res.statusText}: ${body}`);
  }
  return res.json() as Promise<T>;
}

async function postSpark(path: string, params: Record<string, string | number | undefined> = {}): Promise<SparkRunResult> {
  const qs = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== "") qs.set(k, String(v));
  }
  const url = qs.toString() ? `${BASE}${path}?${qs}` : `${BASE}${path}`;
  const res = await fetch(url, { method: "POST", headers: authHeaders() });
  if (!res.ok) {
    const body = await res.text();
    throw new Error(`${res.status} ${res.statusText}: ${body}`);
  }
  return res.json() as Promise<SparkRunResult>;
}

export const tallyAdminApi = {
  installerUrl: async (): Promise<string | null> => {
    const res = await fetch(`${API_BASE_URL}/api/tally-agent/installer`, { headers: authHeaders() });
    if (!res.ok) return null;
    const body = (await res.json()) as { data?: { url?: string | null } };
    const url = body.data?.url;
    return typeof url === "string" && url !== "" ? url : null;
  },
  listCompanies: () => get<{ companies: TallyCompany[] }>("/companies"),
  testConnection: (companyId: number) => postSpark("/test-connection", { "company-id": companyId }),
  pullChartOfAccounts: (companyId: number) => postSpark("/pull-chart-of-accounts", { "company-id": companyId }),
  linkBankAccounts: (companyId: number, opts?: { createMissing?: boolean; dryRun?: boolean; ledgerName?: string }) =>
    postSpark("/link-bank-accounts", {
      "company-id": companyId,
      "create-missing": opts?.createMissing ? 1 : undefined,
      dry: opts?.dryRun ? 1 : undefined,
      "ledger-name": opts?.ledgerName,
    }),
  backfillPartyKeys: (dryRun: boolean) => postSpark("/backfill-party-keys", dryRun ? { dry: 1 } : {}),
  backfillVoucherNumbers: (companyId: number) => postSpark("/backfill-voucher-numbers", { "company-id": companyId }),
  taxLedgersCreate: (companyId: number, taxType: TaxType) =>
    postSpark("/tax-ledgers-create", { "company-id": companyId, "tax-type": taxType }),
  syncTrialBalance: (companyId: number, from?: string, to?: string) =>
    postSpark("/sync-trial-balance", { "company-id": companyId, from, to }),
  backsync: (alsoProcess: boolean) => postSpark("/backsync", alsoProcess ? { "also-process": 1 } : {}),
  processBacksync: (companyId: number | undefined, limit: number, dryRun: boolean) =>
    postSpark("/process-backsync", {
      "company-id": companyId && companyId > 0 ? companyId : undefined,
      limit,
      dry: dryRun ? 1 : undefined,
    }),
};
